import React, { useRef } from 'react';
import { Printer, Download, Mail, Share2, Trash2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Listing } from '@/lib/index';
import { generatePrintHTML } from '@/lib/printExport';
import { toast } from 'sonner';

interface ActionBarProps {
  listing: Listing;
  onDelete: () => void;
}

export function ActionBar({ listing, onDelete }: ActionBarProps) {
  const downloadRef = useRef<HTMLAnchorElement>(null);

  // ── PRINT ──────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const html = generatePrintHTML(listing);
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('Pop-up blocked — please allow pop-ups for this site, then try again.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Wait for resources to load before calling print
    win.onload = () => { win.focus(); win.print(); };
    // Fallback in case onload doesn't fire (e.g. about:blank)
    setTimeout(() => { try { win.focus(); win.print(); } catch (_e) { /* ignore */ } }, 800);
  };

  // ── DOWNLOAD ───────────────────────────────────────────────────────────
  const handleDownload = () => {
    try {
      const html = generatePrintHTML(listing);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const filename = `SellerNetSheet_${(listing.sellerName || 'listing')
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .slice(0, 60)}.html`;

      // Must append to DOM and use dispatchEvent for reliable cross-browser support
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      document.body.removeChild(a);

      // Revoke after a short delay to allow the download to start
      setTimeout(() => URL.revokeObjectURL(url), 3000);

      toast.success('Net sheet downloaded! Open the file in any browser to view or print.');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed. Please try again.');
    }
  };

  // ── EMAIL TO CLIENT ────────────────────────────────────────────────────
  const handleEmail = () => {
    try {
      const sellerName = listing.sellerName || 'Client';
      const property = listing.property || 'your property';

      const subject = `Seller Estimated Net Sheet – ${property}`;
      const body = [
        `Dear ${sellerName},`,
        '',
        `I have prepared your Seller Estimated Net Sheet for ${property}.`,
        '',
        'This document outlines three pricing scenarios and your estimated net proceeds for each, taking into account all closing costs, mortgage payoffs, and fees.',
        '',
        'Please review the attached file and feel free to reach out with any questions or if you would like to adjust any of the figures.',
        '',
        'Looking forward to working with you!',
        '',
        'Warm regards,',
        'Danielle Edwards',
        'RE/MAX Real Estate',
      ].join('\n');

      const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      // Use a hidden <a> with target="_blank" so it does NOT navigate the current page
      const a = document.createElement('a');
      a.href = mailtoLink;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      document.body.removeChild(a);

      toast.success('Email client opened! Download the file first and attach it to the email.', {
        duration: 6000,
      });
    } catch (err) {
      console.error('Email failed:', err);
      toast.error('Could not open email client. Please copy the page link and share manually.');
    }
  };

  // ── SHARE / COPY LINK ──────────────────────────────────────────────────
  const handleShare = async () => {
    const url = window.location.href;

    // Try Web Share API first (mobile / supported browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Seller Net Sheet – ${listing.sellerName || 'Listing'}`,
          text: `Seller Estimated Net Sheet for ${listing.property || 'property'}`,
          url,
        });
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to clipboard
        if ((err as DOMException).name === 'AbortError') return;
      }
    }

    // Fallback: clipboard copy
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
        return;
      } catch (_e) {
        // Clipboard permission denied — fall through to execCommand
      }
    }

    // Last resort: execCommand (works without permissions)
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (success) {
        toast.success('Link copied to clipboard!');
      } else {
        toast.error('Could not copy. Please copy the URL from the address bar.');
      }
    } catch (err) {
      toast.error('Could not copy. Please copy the URL from the address bar.');
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        onClick={handlePrint}
        variant="outline"
        size="sm"
        className="gap-1.5 border-border text-foreground hover:bg-muted"
      >
        <Printer size={14} />
        Print
      </Button>
      <Button
        onClick={handleDownload}
        variant="outline"
        size="sm"
        className="gap-1.5 border-border text-foreground hover:bg-muted"
      >
        <Download size={14} />
        Download
      </Button>
      <Button
        onClick={handleEmail}
        size="sm"
        className="gap-1.5 bg-primary text-primary-foreground hover:opacity-90"
      >
        <Mail size={14} />
        Email to Client
      </Button>
      <Button
        onClick={handleShare}
        variant="ghost"
        size="sm"
        className="gap-1.5 text-muted-foreground hover:text-foreground"
      >
        <Share2 size={14} />
        Share
      </Button>
      <Button
        onClick={onDelete}
        variant="ghost"
        size="sm"
        className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
      >
        <Trash2 size={14} />
        Delete
      </Button>
    </div>
  );
}
